import { ImageGrid } from "#src/index.ts";

import Bento from "../components/bento.gts";
import CssAndJs from "../components/css-and-js.gts";
import Playground from "../components/playground.gts";
import UnknownDimensions from "../components/unknown-dimensions.gts";
import { photos } from "../images.ts";

<template>
  <div class="demo">
    <header>
      <h1>ImageGrid</h1>
      <p>
        A responsive image grid that balances each row to fill the available width. Resize the
        window to watch the rows re-pack.
      </p>
    </header>

    <section>
      <h2>CSS and JS</h2>
      <p>
        The CSS module does the base layout: a wrapping flex row of fixed-height, cropped images. On
        its own that leaves ragged right edges. The
        <code>balanceRows</code>
        modifier is the JS layer that measures each row and scales it to fill the width. Flip the
        switch to see exactly what the JS adds; the markup and CSS are identical either way, the
        only difference is the modifier.
      </p>
      <CssAndJs />
    </section>

    <section>
      <h2>Playground</h2>
      <p>
        Toggle the options and watch the grid re-balance.
        <code>@preserve</code>
        chooses whether a row grows taller to keep every image's aspect ratio (<code
        >aspect-ratio</code>) or pins the row height and crops widths (<code>height</code>).
        <code>@aggressive</code>
        spreads items evenly across rows and always justifies the last row to fill the width
        (letting its height change in aspect-ratio mode), so the block stays flush; without it, a
        sparse last row is left at its natural size. Drop the image count to an odd number to see
        that last-row difference clearly.
      </p>
      <p>
        In aspect-ratio mode
        <code>@rowHeight</code>
        is only a target. Filling each row's width while keeping every image's true aspect ratio
        means the height has to give — you can't have all three at once — so the rows vary in height
        and
        <code>@rowHeight</code>
        mainly influences how many images pack onto a row. Switch to
        <code>height</code>
        mode to pin the height exactly, at the cost of cropping the images.
      </p>
      <Playground />
    </section>

    <section>
      <h2>Custom items</h2>
      <p>
        Pass your own objects to
        <code>@items</code>
        and point
        <code>@key</code>
        at the image inside each one. Here every item is a
        <code>{ label, preview }</code>
        with
        <code>@key="preview"</code>. The block then yields two values:
        <code>as |photo image|</code>
        gives you the original item first (so you can show
        <code>photo.label</code>), then the
        <code>Image</code>
        the grid resolved from it (<code>photo.preview</code>, used for the
        <code>src</code>).
      </p>
      <ImageGrid @items={{photos}} @key="preview" as |photo image|>
        <figure>
          <img src={{image.src}} alt={{photo.label}} loading="lazy" />
          <figcaption>{{photo.label}}</figcaption>
        </figure>
      </ImageGrid>
    </section>

    <section>
      <h2>Modifier only (advanced)</h2>
      <p>
        The
        <code>balanceRows</code>
        modifier takes no image data. It justifies whatever direct children you give it, so it works
        on any content, not only images. These are plain content cards, each with its own aspect
        ratio, packed into balanced rows just like photos would be. You own the markup and the CSS
        (the library ships none for this path);
        <code>selector</code>
        points the modifier at the cells. Toggle
        <em>Balance rows</em>
        off to see what the CSS does alone (fixed-height cards at their natural widths, with ragged
        rows); the modifier justifies them into filled rows. The aspect ratios come from CSS
        classes, and a container query reshapes the extreme ones as the container narrows, so drag
        the width control to watch the cells re-pack and change shape.
      </p>
      <Bento />
    </section>

    <section>
      <h2>Unknown dimensions</h2>
      <p>
        These images are passed with only a
        <code>src</code>
        and no
        <code>width</code>
        or
        <code>height</code>. Each one's real size is read from its
        <code>load</code>
        event, so the grid lays itself out correctly with no dimensions known up front.
      </p>
      <p>
        This matters most when you genuinely can't know the sizes ahead of time: an upload preview
        grid, where the user has just picked files off their file system and nothing has measured
        them yet, or images from an API you don't control that returns URLs without any width or
        height metadata. In both cases you render what you have and let each image report its own
        size on load.
      </p>
      <UnknownDimensions />
    </section>
  </div>
</template>
